import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Edit, LocationOn } from '@mui/icons-material';

/**
 * AddressDisplay - Standardized address rendering component
 * 
 * @param {object} address - Address object with fields: address_1, address_2, city, state, zip_code, country
 * @param {string} variant - Display mode: 'inline' | 'compact' | 'detailed'
 * @param {boolean} showIcon - Whether to show location pin icon (for compact variant)
 * @param {function} onEdit - Optional edit handler (shows edit button if provided)
 * @param {boolean} showDefaultRadius - Whether to display default_radius field
 */
const AddressDisplay = ({
  address = {},
  variant = 'compact',
  showIcon = true,
  onEdit,
  showDefaultRadius = false,
  sx = {},
}) => {
  if (!address) return null;

  // Inline variant: single line format (Address_1, City, State Zip, Country)
  if (variant === 'inline') {
    const parts = [];
    if (address.address_1) parts.push(address.address_1);
    if (address.city) parts.push(address.city);
    if (address.state && address.zip_code) {
      parts.push(`${address.state} ${address.zip_code}`);
    } else if (address.state) {
      parts.push(address.state);
    }
    if (address.country) parts.push(address.country);
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ...sx }}>
        <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
          {parts.length > 0 ? parts.join(', ') : 'N/A'}
        </Typography>
        {onEdit && (
          <IconButton size="small" onClick={onEdit} sx={{ color: '#666666' }}>
            <Edit fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  // Compact variant: formatted multi-line with optional location icon
  // Format:
  //   Address_1, Address_2
  //   City, State Zip
  //   Country
  if (variant === 'compact') {
    // Build address lines
    const line1Parts = [address.address_1];
    if (address.address_2) line1Parts.push(address.address_2);
    const line1 = line1Parts.filter(Boolean).join(', ');

    const line2Parts = [];
    if (address.city) line2Parts.push(address.city);
    if (address.state && address.zip_code) {
      line2Parts.push(`${address.state} ${address.zip_code}`);
    } else if (address.state) {
      line2Parts.push(address.state);
    } else if (address.zip_code) {
      line2Parts.push(address.zip_code);
    }
    const line2 = line2Parts.join(', ');

    const line3 = address.country || '';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...sx }}>
        {/* Address with icon and optional edit button */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {showIcon && (
            <LocationOn sx={{ color: '#333', fontSize: 24, mt: 0.25 }} />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {line1 && (
              <Typography variant="body2" color="text.primary">
                {line1}
              </Typography>
            )}
            {line2 && (
              <Typography variant="body2" color="text.primary">
                {line2}
              </Typography>
            )}
            {line3 && (
              <Typography variant="body2" color="text.primary">
                {line3}
              </Typography>
            )}
          </Box>
          {onEdit && (
            <IconButton
              size="small"
              onClick={onEdit}
              sx={{ color: '#666666', ml: 1 }}
              title="Edit Address"
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Default Radius */}
        {showDefaultRadius && address.default_radius && (
          <Typography variant="body2" color="text.primary">
            Default Radius: {address.default_radius} {address.default_radius === 1 ? 'mile' : 'miles'}
          </Typography>
        )}
      </Box>
    );
  }

  // Detailed variant: each field on its own line with labels
  const fields = [
    { label: 'Address 1', value: address.address_1 },
    address.address_2 && { label: 'Address 2', value: address.address_2 },
    { label: 'City', value: address.city },
    { label: 'State', value: address.state },
    { label: 'Zip', value: address.zip_code },
    address.country && { label: 'Country', value: address.country },
    showDefaultRadius && address.default_radius && {
      label: 'Default Radius',
      value: `${address.default_radius} ${address.default_radius === 1 ? 'mile' : 'miles'}`,
    },
  ].filter(Boolean);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
      {fields.map((field, index) => (
        <Typography key={index} variant="body2" color="text.secondary">
          {field.label}: {field.value}
        </Typography>
      ))}
    </Box>
  );
};

export default AddressDisplay;
